import { TimelineItem } from "@/models/legislation/Timeline";
import {
  DOCUMENT_TYPE_ID_AENDRINGSBEKENDTGØRELSE,
  DOCUMENT_TYPE_ID_BEKENDTGØRELSE,
} from "@/utils/legislationDocumentTypeUtils";

/**
 * Flatten a lineage timeline via naive depth-first recursion over `children`,
 * preserving the tree traversal order. Laws are flat arrays; a Bekendtgørelse
 * timeline is a nested BEK (60) tree whose branch structure is kept intact so
 * the timeline can be traversed via branches (consolidations are plotted onto
 * the tree nodes by accession rather than by flattening the tree away).
 */
export const flattenTimelineItems = (
  items: TimelineItem[] | undefined
): TimelineItem[] => {
  if (!items?.length) return [];
  return items.flatMap((item) => [
    item,
    ...(item.children ? flattenTimelineItems(item.children) : []),
  ]);
};

const isBekOrAendringsBek = (documentTypeId: string | number | undefined | null): boolean => {
  const num = Number(documentTypeId);
  return (
    num === DOCUMENT_TYPE_ID_BEKENDTGØRELSE ||
    num === DOCUMENT_TYPE_ID_AENDRINGSBEKENDTGØRELSE
  );
};

/**
 * True for a Bekendtgørelse family timeline. Mirrors the API `IsBekFamily`: the
 * row's own document type being BEK (60) or Ændringsbekendtgørelse (80) is
 * decisive; otherwise a single-root tree whose root is a 60/80 with nested
 * `children` also qualifies. Keeping this in sync with the API ensures viewer
 * consolidation counts scope the same way as list/search.
 */
export const isBekFamilyTimeline = (
  items: TimelineItem[] | undefined,
  documentTypeId?: string | number | null
): boolean => {
  if (documentTypeId != null && isBekOrAendringsBek(documentTypeId)) return true;
  return (
    items?.length === 1 &&
    isBekOrAendringsBek(items[0].documentTypeId) &&
    Boolean(items[0].children?.length)
  );
};

/**
 * Preorder flatten of a Bekendtgørelse timeline keeping only BEK (60) and
 * Ændringsbekendtgørelse (80) nodes, skipping nested Lov (10) references that
 * must not inflate consolidation window counts. Mirrors the API
 * `FlattenBekConsolidationLineage` so viewer denominators match list/search.
 */
export const flattenBekConsolidationLineage = (
  items: TimelineItem[] | undefined
): TimelineItem[] => {
  if (!items?.length) return [];

  const flat: TimelineItem[] = [];
  const seen = new Set<string>();

  const walk = (node: TimelineItem): void => {
    const accession = node.accessionNumber;
    const typeId = Number(node.documentTypeId);
    if (
      accession &&
      !seen.has(accession) &&
      (typeId === DOCUMENT_TYPE_ID_BEKENDTGØRELSE ||
        typeId === DOCUMENT_TYPE_ID_AENDRINGSBEKENDTGØRELSE)
    ) {
      seen.add(accession);
      flat.push(node);
    }
    for (const child of node.children ?? []) walk(child);
  };

  for (const root of items) walk(root);
  return flat;
};

/**
 * Consolidation-scoped flatten: BEK families skip nested Lov refs (matching the
 * API), every other timeline uses the full traversal. Pass the row's
 * `documentTypeId` so a BEK/Ændringsbekendtgørelse row is detected even when its
 * timeline is not a single 60/80-root tree.
 */
export const flattenConsolidationTimeline = (
  items: TimelineItem[] | undefined,
  documentTypeId?: string | number | null
): TimelineItem[] =>
  isBekFamilyTimeline(items, documentTypeId)
    ? flattenBekConsolidationLineage(items)
    : flattenTimelineItems(items);

export const resolveTimelineLabel = (
  accessionNumber: string,
  timelineItems: TimelineItem[] | undefined
): string => {
  const match = flattenTimelineItems(timelineItems).find(
    (item) => item.accessionNumber === accessionNumber
  );
  if (!match) return accessionNumber;
  return match.shortName || match.title || accessionNumber;
};

export type ConsolidationHistoricWatermarkOptions = {
  isConsolidatedRender: boolean;
  isConsolidationNotFound: boolean;
  /** Anchor LBK the consolidated text is built from (render context `lawExternalId`). */
  anchorLawAccession?: string | null;
  /** Route document id — the anchor itself unless a changelaw page renders the consolidation. */
  documentExternalId?: string | null;
  legislationIsCurrent?: boolean | null;
  timelineItems?: TimelineItem[];
};

const resolveTimelineItemIsHistoric = (
  accessionNumber: string,
  timelineItems: TimelineItem[] | undefined
): boolean | undefined =>
  flattenTimelineItems(timelineItems).find(
    (item) => item.accessionNumber === accessionNumber
  )?.isHistoric;

/**
 * Whether to show the historic "Ikke-gældende" watermark on consolidated views.
 *
 * A consolidation is the anchor LBK plus the amendments folded into it, so it is
 * historic exactly when that LBK is historic (superseded by a newer LBK or
 * repealed) — the coverage date of the incorporated amendments says nothing about
 * validity. On an anchor route the loaded silver row is the LBK itself, so its
 * `is_current` decides; changelaw routes read `isHistoric` off the anchor's
 * timeline node instead. Non-consolidated (silver) renders fall back to the
 * viewed row's own `is_current`.
 */
export const shouldShowConsolidationHistoricWatermark = ({
  isConsolidatedRender,
  isConsolidationNotFound,
  anchorLawAccession,
  documentExternalId,
  legislationIsCurrent,
  timelineItems,
}: ConsolidationHistoricWatermarkOptions): boolean => {
  if (!isConsolidatedRender || isConsolidationNotFound) {
    return legislationIsCurrent !== true;
  }

  const anchor = anchorLawAccession?.trim();
  if (!anchor) return legislationIsCurrent === false;

  const isAnchorRoute = documentExternalId?.trim() === anchor;
  if (isAnchorRoute && legislationIsCurrent != null) {
    return !legislationIsCurrent;
  }

  return resolveTimelineItemIsHistoric(anchor, timelineItems) === true;
};
